import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User, UserRole, AuthState } from '../types/auth.types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (user: User) => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  checkTokenValidity: () => Promise<boolean>;
}

const defaultAuthContext: AuthContextType = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => Promise.resolve(false),
  logout: () => {},
  updateUser: () => {},
  hasRole: () => false,
  hasAnyRole: () => false,
  checkTokenValidity: () => Promise.resolve(false)
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false
  });
  const [isLoading, setIsLoading] = useState(true);

  // Initialiser l'état d'authentification à partir du localStorage
  useEffect(() => {
    console.log("Initialisation de l'état d'authentification");
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser);
        setAuthState({
          user,
          token: storedToken,
          isAuthenticated: true
        });
      } catch (error) {
        console.error('Erreur lors du parsing des données utilisateur stockées:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    
    setIsLoading(false);
  }, []);  // Ajouter une fonction pour vérifier si le token est toujours valide
  const checkTokenValidity = async (): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      // Tenter de récupérer le compte pour vérifier si le token est valide
      await authService.getAccount();
      return true;
    } catch (error) {
      console.error('Token invalide ou expiré:', error);
      logout(); // Déconnecter l'utilisateur si le token est invalide
      return false;
    }
  };
  
  const login = async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      console.log('Tentative de connexion avec:', { username });
      const credentials = { username, password };
      const response = await authService.login(credentials);

      if (response && response.data && response.data.id_token) {
        // Stocker le token JWT
        const token = response.data.id_token;
        localStorage.setItem('token', token);
        
        // Récupérer les informations de l'utilisateur à partir de /api/account
        try {
          const accountResponse = await authService.getAccount();
          if (accountResponse && accountResponse.data) {
            const user = accountResponse.data;
            localStorage.setItem('user', JSON.stringify(user));
            
            setAuthState({
              user,
              token,
              isAuthenticated: true
            });
            
            return true;
          }
        } catch (accountError) {
          console.error('Erreur lors de la récupération du compte:', accountError);
          // Si on ne peut pas récupérer le compte, on considère que la connexion a échoué
          localStorage.removeItem('token');
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false
    });
    
    authService.logout();
  };

  const updateUser = (user: User) => {
    setAuthState(prev => ({ 
      ...prev, 
      user,
    }));
    localStorage.setItem('user', JSON.stringify(user));
  };
  return (
    <AuthContext.Provider value={{
      user: authState.user,
      token: authState.token,
      isAuthenticated: authState.isAuthenticated,
      isLoading,
      login,
      logout,
      checkTokenValidity,
      updateUser,      hasRole: (role: UserRole) => {
        if (!authState.user || !authState.user.authorities) return false;
        return authState.user.authorities.includes(role);
      },
      hasAnyRole: (roles: UserRole[]) => {
        console.log("hasAnyRole called with roles:", roles)
        console.log("authState.user.authorities:", authState.user?.authorities)
        console.log(roles.some(role => authState.user!.authorities.includes(role)))
        if (!authState.user || !authState.user.authorities) return false;
        return roles.some(role => authState.user!.authorities.includes(role));
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
